
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { localShopPriceService } from '@/services/localShopPriceService';
import { CreateLocalShopPriceInput, LocalShopPriceFilters } from '@/types/database/local-shop-prices';
import { useToast } from '@/hooks/use-toast';

export const useLocalShopPrices = (filters?: LocalShopPriceFilters) => {
  return useQuery({
    queryKey: ['local-shop-prices', filters],
    queryFn: () => localShopPriceService.getLocalShopPrices(filters),
  });
};

export const useCreateLocalShopPrice = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (price: CreateLocalShopPriceInput) =>
      localShopPriceService.createLocalShopPrice(price),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['local-shop-prices'] });
      toast({
        title: 'Success',
        description: 'Local shop price created successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create price',
        variant: 'destructive',
      });
    },
  });
};

export const useBulkCreateLocalShopPrices = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (prices: CreateLocalShopPriceInput[]) =>
      localShopPriceService.bulkCreateLocalShopPrices(prices),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['local-shop-prices'] });
      toast({
        title: 'Success',
        description: `Successfully uploaded ${data.length} local shop prices`,
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to upload prices',
        variant: 'destructive',
      });
    },
  });
};

export const useUpdateLocalShopPrice = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, price }: { id: string; price: Partial<CreateLocalShopPriceInput> }) =>
      localShopPriceService.updateLocalShopPrice(id, price),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['local-shop-prices'] });
      toast({
        title: 'Success',
        description: 'Local shop price updated successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update price',
        variant: 'destructive',
      });
    },
  });
};

export const useDeleteLocalShopPrice = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => localShopPriceService.deleteLocalShopPrice(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['local-shop-prices'] });
      toast({
        title: 'Success',
        description: 'Local shop price deleted successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete price',
        variant: 'destructive',
      });
    },
  });
};
